import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axios";
import { toast } from 'react-toastify';

function DoctorRegister() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  /* ---------------- CSRF INITIALIZATION ---------------- */
  useEffect(() => {
    const initCsrf = async () => {
      try {
        await api.get('doctor/login/');
      } catch (err) {
        console.error("CSRF Init failed", err);
      }
    };
    initCsrf();
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone_number: "",
    address: "",
    specialization: "",
    qualification: "",
    experience: "",
    identity_document: null,
    medical_certificate: null,
    experience_certificate: null,
    consultation_fees: "",
    audio_consultation_fees: "",
    image: null,
  });

  const [passwordValid, setPasswordValid] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });
  const [showPasswordHint, setShowPasswordHint] = useState(false);

  const validatePassword = (pass) => {
    setPasswordValid({
      length: pass.length >= 8,
      uppercase: /[A-Z]/.test(pass),
      lowercase: /[a-z]/.test(pass),
      number: /\d/.test(pass),
      special: /[@$!%*#?&]/.test(pass)
    });
  };

  const refs = {
    name: useRef(),
    email: useRef(),
    password: useRef(),
    phone_number: useRef(),
    address: useRef(),
    specialization: useRef(),
    qualification: useRef(),
    experience: useRef(),
    identity_document: useRef(),
    medical_certificate: useRef(),
    experience_certificate: useRef(),
    consultation_fees: useRef(),
    audio_consultation_fees: useRef(),
    image: useRef(),
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === 'password') {
      validatePassword(value);
    }
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.files[0] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);

    // 🔐 Password strength validation (same rule as user registration)
    const password = formData.password;
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/;
    if (!passwordRegex.test(password)) {
      toast.warning(
        'Password must contain at least 1 uppercase letter, 1 lowercase letter, 1 number, 1 special character and minimum 8 characters.',
        {
          position: "bottom-right",
          autoClose: 4000,
        }
      );
      setLoading(false);
      return;
    }

    const data = new FormData();
    Object.keys(formData).forEach((key) => {
      if (formData[key] !== null) data.append(key, formData[key]);
    });

    try {
      const res = await api.post("doctor/register/", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data.message || "Registration successful! Please wait for admin approval.", {
        position: "bottom-right",
        autoClose: 3000,
      });
      navigate("/doctor/login");
    } catch (err) {
      if (err.response?.data) {
        setErrors(err.response.data);
        const firstField = Object.keys(err.response.data)[0];
        refs[firstField]?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        toast.error("Registration failed. Please check your details.", {
          position: "bottom-right",
          autoClose: 3000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const renderError = (field) => {
    if (!errors[field]) return null;
    return (
      <div className="text-danger small mt-1">
        <i className="bi bi-exclamation-circle me-1"></i>
        {Array.isArray(errors[field]) ? errors[field].join(", ") : errors[field]}
      </div>
    );
  };

  return (
    <div className="bg-light min-vh-100 py-5">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-10 col-lg-8">
            
            <div className="text-center mb-4">
              <div className="bg-primary d-inline-flex p-3 rounded-circle shadow-sm mb-3">
                <i className="bi bi-person-plus-fill fs-2 text-white"></i>
              </div>
              <h2 className="fw-bold text-primary">Join FITNOVA as a Doctor</h2>
              <p className="text-muted">Register to provide high-quality digital healthcare services.</p>
            </div>

            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-body p-4 p-md-5">
                <form onSubmit={handleSubmit} encType="multipart/form-data">
                  
                  <h5 className="fw-bold mb-4 pb-2 border-bottom">
                    <i className="bi bi-info-circle me-2 text-primary"></i>Personal Information
                  </h5>

                  <div className="row g-3 mb-4">
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">Full Name</label>
                      <input
                        ref={refs.name}
                        type="text"
                        name="name"
                        className={`form-control bg-light ${errors.name ? 'is-invalid' : ''}`}
                        placeholder="Dr. John Doe"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                      {renderError("name")}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">Email Address</label>
                      <input
                        ref={refs.email}
                        type="email"
                        name="email"
                        className={`form-control bg-light ${errors.email ? 'is-invalid' : ''}`}
                        placeholder="doctor@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                      {renderError("email")}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">Password</label>
                      <input
                        ref={refs.password}
                        type="password"
                        name="password"
                        className={`form-control bg-light ${errors.password ? 'is-invalid' : ''}`}
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={handleChange}
                        onFocus={() => setShowPasswordHint(true)}
                        onBlur={() => setShowPasswordHint(false)}
                        required
                      />
                      {showPasswordHint && (
                        <div className="card shadow-sm mt-2 p-3 position-absolute z-3 w-100" style={{ maxWidth: '300px' }}>
                          <h6 className="small fw-bold border-bottom pb-2 mb-2">Password Requirements:</h6>
                          <div className={`small mb-1 ${passwordValid.length ? 'text-success' : 'text-muted'}`}>
                            <i className={`bi bi-${passwordValid.length ? 'check-circle-fill' : 'circle'} me-2`}></i>
                            At least 8 characters
                          </div>
                          <div className={`small mb-1 ${passwordValid.uppercase ? 'text-success' : 'text-muted'}`}>
                            <i className={`bi bi-${passwordValid.uppercase ? 'check-circle-fill' : 'circle'} me-2`}></i>
                            One uppercase letter
                          </div>
                          <div className={`small mb-1 ${passwordValid.lowercase ? 'text-success' : 'text-muted'}`}>
                            <i className={`bi bi-${passwordValid.lowercase ? 'check-circle-fill' : 'circle'} me-2`}></i>
                            One lowercase letter
                          </div>
                          <div className={`small mb-1 ${passwordValid.number ? 'text-success' : 'text-muted'}`}>
                            <i className={`bi bi-${passwordValid.number ? 'check-circle-fill' : 'circle'} me-2`}></i>
                            One number
                          </div>
                          <div className={`small ${passwordValid.special ? 'text-success' : 'text-muted'}`}>
                            <i className={`bi bi-${passwordValid.special ? 'check-circle-fill' : 'circle'} me-2`}></i>
                            One special character (@$!%*#?&)
                          </div>
                        </div>
                      )}
                      {renderError("password")}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">Phone Number</label>
                      <input
                        ref={refs.phone_number}
                        type="tel"
                        name="phone_number"
                        maxLength={10}
                        className={`form-control bg-light ${errors.phone_number ? 'is-invalid' : ''}`}
                        placeholder="+1 234 567 890"
                        value={formData.phone_number}
                        onChange={handleChange}
                      />
                      {renderError("phone_number")}
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-bold">Clinic / Home Address</label>
                      <textarea
                        ref={refs.address}
                        name="address"
                        className={`form-control bg-light ${errors.address ? 'is-invalid' : ''}`}
                        placeholder="Street, City, Country"
                        rows="2"
                        value={formData.address}
                        onChange={handleChange}
                      />
                      {renderError("address")}
                    </div>
                  </div>

                  <h5 className="fw-bold mb-4 pb-2 border-bottom">
                    <i className="bi bi-person-badge me-2 text-primary"></i>Professional Credentials
                  </h5>

                  <div className="row g-3 mb-4">
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">Specialization</label>
                      <input
                        ref={refs.specialization}
                        type="text"
                        name="specialization"
                        className={`form-control bg-light ${errors.specialization ? 'is-invalid' : ''}`}
                        placeholder="e.g. Cardiology"
                        value={formData.specialization}
                        onChange={handleChange}
                      />
                      {renderError("specialization")}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">Qualification</label>
                      <input
                        ref={refs.qualification}
                        type="text"
                        name="qualification"
                        className={`form-control bg-light ${errors.qualification ? 'is-invalid' : ''}`}
                        placeholder="e.g. MBBS, MD"
                        value={formData.qualification}
                        onChange={handleChange}
                      />
                      {renderError("qualification")}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">Experience (Years)</label>
                      <input
                        ref={refs.experience}
                        type="number"
                        name="experience"
                        className={`form-control bg-light ${errors.experience ? 'is-invalid' : ''}`}
                        placeholder="e.g. 5"
                        value={formData.experience}
                        onChange={handleChange}
                      />
                      {renderError("experience")}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">Video Consultation Fees (USD $)</label>
                      <input
                        ref={refs.consultation_fees}
                        type="number"
                        name="consultation_fees"
                        className={`form-control bg-light ${errors.consultation_fees ? 'is-invalid' : ''}`}
                        placeholder="Video call fee in USD ($)"
                        value={formData.consultation_fees}
                        onChange={handleChange}
                        required
                      />
                      {renderError("consultation_fees")}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">Audio Consultation Fees (USD $)</label>
                      <input
                        ref={refs.audio_consultation_fees}
                        type="number"
                        name="audio_consultation_fees"
                        className={`form-control bg-light ${errors.audio_consultation_fees ? 'is-invalid' : ''}`}
                        placeholder="Audio call fee in USD ($)"
                        value={formData.audio_consultation_fees}
                        onChange={handleChange}
                        required
                      />
                      {renderError("audio_consultation_fees")}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">Profile Image</label>
                      <input
                        ref={refs.image}
                        type="file"
                        name="image"
                        accept="image/*"
                        className={`form-control bg-light ${errors.image ? 'is-invalid' : ''}`}
                        onChange={handleFileChange}
                      />
                      {renderError("image")}
                    </div>
                  </div>

                  <h5 className="fw-bold mb-4 pb-2 border-bottom">
                    <i className="bi bi-file-earmark-pdf me-2 text-primary"></i>Documents & Verification
                  </h5>

                  <div className="row g-3 mb-4">
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">Identity Document (PDF/Image)</label>
                      <input
                        ref={refs.identity_document}
                        type="file"
                        name="identity_document"
                        className={`form-control bg-light ${errors.identity_document ? 'is-invalid' : ''}`}
                        onChange={handleFileChange}
                      />
                      {renderError("identity_document")}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">Medical Certificate</label>
                      <input
                        ref={refs.medical_certificate}
                        type="file"
                        name="medical_certificate"
                        className={`form-control bg-light ${errors.medical_certificate ? 'is-invalid' : ''}`}
                        onChange={handleFileChange}
                      />
                      {renderError("medical_certificate")}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">Experience Certificate</label>
                      <input
                        ref={refs.experience_certificate}
                        type="file"
                        name="experience_certificate"
                        className={`form-control bg-light ${errors.experience_certificate ? 'is-invalid' : ''}`}
                        onChange={handleFileChange}
                      />
                      {renderError("experience_certificate")}
                    </div>
                  </div>

                  <div className="d-grid gap-3 pt-3">
                    <button 
                      type="submit" 
                      className="btn btn-primary btn-lg fw-bold rounded-3 shadow-sm d-flex align-items-center justify-content-center"
                      disabled={loading}
                    >
                      {loading && <span className="spinner-border spinner-border-sm me-2"></span>}
                      Submit Registration
                    </button>
                    
                    <button 
                      type="button" 
                      className="btn btn-link btn-sm text-decoration-none text-muted"
                      onClick={() => navigate('/doctor/login')}
                    >
                      Already have a doctor account? <span className="text-primary fw-bold">Login here</span>
                    </button>
                  </div>

                </form>
              </div>
            </div>

            <div className="text-center mt-4">
              <button 
                className="btn btn-link btn-sm text-muted text-decoration-none"
                onClick={() => navigate(-1)}
              >
                <i className="bi bi-arrow-left me-1"></i> Back
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export default DoctorRegister;
